<template>
  <div class="all-queries-container">
    <!-- Header -->
    <div class="queries-header">
      <h2 class="header-title">All Search History</h2>
      <div class="total-count">{{ total }} queries total</div>
    </div>

    <!-- Search & Filter -->
    <div class="search-wrapper">
      <div class="search-row">
        <div class="search-input-container">
          <font-awesome-icon icon="magnifying-glass" class="search-icon" />
          <input
            v-model="searchInput"
            type="text"
            class="search-input"
            placeholder="Search queries..."
            @input="handleSearchInput"
          />
        </div>
        <select v-model="timeFilter" class="time-filter" @change="handleTimeFilterChange">
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="week">Last Week</option>
          <option value="older">Older</option>
        </select>
      </div>
    </div>

    <!-- Loading State (initial load) -->
    <div v-if="loading && queries.length === 0" class="loading-container">
      <font-awesome-icon icon="spinner" class="spinner" spin />
      <p class="loading-text">{{ searchInput ? 'Searching...' : 'Loading queries...' }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <font-awesome-icon icon="triangle-exclamation" class="error-icon" />
      <p class="error-title">Failed to load queries</p>
      <p class="error-message">{{ error }}</p>
      <button class="retry-button" @click="resetAndSearch">
        Try Again
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="queries.length === 0 && !loading" class="empty-state">
      <font-awesome-icon icon="magnifying-glass" class="empty-icon" />
      <p class="empty-title">{{ searchInput ? 'No queries found' : 'No search history' }}</p>
      <p class="empty-description">
        {{ searchInput ? 'Try a different search term' : 'Your search history will appear here' }}
      </p>
    </div>

    <!-- Query List -->
    <div v-else ref="queriesList" class="queries-list">
      <div class="history-grid">
        <div
          v-for="query in queries"
          :key="query.id"
          class="history-chip"
          :class="{ 'event-type-chip': EVENT_TYPES.includes(query.type) }"
          @click="exec_query(query.query, query.type)"
        >
          <div class="history-chip-left">
            <span
              class="history-type"
              :class="{ 'event-type': EVENT_TYPES.includes(query.type) }"
            >
              {{ pretty_print(query.type) }}
            </span>
            <span class="history-time">{{ moment(query.timestamp).calendar() }}</span>
          </div>
          <div class="history-query-content">
            <code class="history-query">{{ EVENT_TYPES.includes(query.type) ? parseQuery(query.query).text : query.query }}</code>
            <span v-if="EVENT_TYPES.includes(query.type)" class="time-badge">{{ parseQuery(query.query).timeLabel }}</span>
          </div>
          <div class="history-credits">{{ query.credits_used }} credits</div>
        </div>
      </div>

      <!-- Intersection Observer Trigger -->
      <div ref="loadMoreTrigger" class="load-more-trigger"></div>

      <!-- Loading More Indicator -->
      <div v-if="loadingMore" class="loading-more">
        <font-awesome-icon icon="spinner" class="spinner-small" spin />
        <span>Loading more...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import moment from 'moment'
import User from '@/api/User'
import { extractTimeFromQuery, formatTimeRange } from '@/components/EventSearch/timeRangeUtils'

const router = useRouter()

const props = defineProps({
  filterType: {
    type: String,
    default: null,
    validator: (value) => [null, 'ip', 'event'].includes(value)
  }
})

const EVENT_TYPES = ['event', 'event_search']

// State
const queries = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const error = ref('')
const searchInput = ref('')
const offset = ref(0)
const limit = 50
const total = ref(0)
const hasMore = ref(false)
const loadMoreTrigger = ref(null)
const queriesList = ref(null)
const timeFilter = ref('all')
let observer = null
let searchTimeout = null

// Compute start/end ISO strings from the selected time filter
const timeRange = computed(() => {
  const now = new Date()
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (timeFilter.value) {
    case 'today':
      return { start: startOfToday.toISOString(), end: '' }
    case 'yesterday': {
      const startOfYesterday = new Date(startOfToday)
      startOfYesterday.setDate(startOfYesterday.getDate() - 1)
      return { start: startOfYesterday.toISOString(), end: startOfToday.toISOString() }
    }
    case 'week': {
      const dayOfWeek = startOfToday.getDay()
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const startOfThisWeek = new Date(startOfToday)
      startOfThisWeek.setDate(startOfThisWeek.getDate() - daysSinceMonday)
      const startOfLastWeek = new Date(startOfThisWeek)
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
      return { start: startOfLastWeek.toISOString(), end: startOfThisWeek.toISOString() }
    }
    case 'older': {
      const dayOfWeek = startOfToday.getDay()
      const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      const startOfThisWeek = new Date(startOfToday)
      startOfThisWeek.setDate(startOfThisWeek.getDate() - daysSinceMonday)
      const startOfLastWeek = new Date(startOfThisWeek)
      startOfLastWeek.setDate(startOfLastWeek.getDate() - 7)
      return { start: '', end: startOfLastWeek.toISOString() }
    }
    default:
      return { start: '', end: '' }
  }
})

// Fetch queries from API
const fetchQueries = async (isLoadMore = false) => {
  if (!hasMore.value && isLoadMore) return
  if (loading.value || loadingMore.value) return

  if (isLoadMore) {
    loadingMore.value = true
  } else {
    loading.value = true
  }

  error.value = ''

  try {
    const { start, end } = timeRange.value
    const response = await User.getQueries(offset.value, limit, searchInput.value, start, end, props.filterType || '')

    if (response.status === 'success') {
      const responseData = response.data || {}
      const queriesArray = responseData.queries || []

      if (isLoadMore) {
        queries.value = [...queries.value, ...queriesArray]
      } else {
        queries.value = queriesArray
      }

      total.value = responseData.total || 0
      hasMore.value = responseData.has_more || false
      offset.value += queriesArray.length
    } else {
      error.value = response.error || 'Failed to load queries'
      hasMore.value = false
    }
  } catch (err) {
    console.error('Error fetching queries:', err)
    error.value = err.response?.data?.message || 'An error occurred while loading queries'
    hasMore.value = false
  } finally {
    loading.value = false
    loadingMore.value = false

    // Re-setup observer after non-loadMore fetches since the queries-list
    // element gets unmounted/remounted by v-else conditional rendering
    if (!isLoadMore) {
      await nextTick()
      setupIntersectionObserver()
    }
  }
}

// Handle search input with debounce (300ms)
const handleSearchInput = () => {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }

  searchTimeout = setTimeout(() => {
    resetAndSearch()
  }, 300)
}

// Handle time filter change
const handleTimeFilterChange = () => {
  resetAndSearch()
}

// Reset offset and search
const resetAndSearch = () => {
  offset.value = 0
  queries.value = []
  hasMore.value = true
  fetchQueries()
}

// Setup intersection observer for infinite scroll
const setupIntersectionObserver = () => {
  if (!loadMoreTrigger.value) return

  // Disconnect previous observer if it exists
  if (observer) {
    observer.disconnect()
    observer = null
  }

  observer = new IntersectionObserver(
    (entries) => {
      const [entry] = entries
      if (entry.isIntersecting && hasMore.value && !loadingMore.value) {
        fetchQueries(true)
      }
    },
    {
      root: queriesList.value,
      rootMargin: '100px',
      threshold: 0.1
    }
  )

  observer.observe(loadMoreTrigger.value)
}

// Execute query (navigate to search results)
async function exec_query(input, query_type) {
  // Route event queries to EventSearch view
  if (EVENT_TYPES.includes(query_type)) {
    router.push({
      name: 'EventSearch',
      query: {
        query: input.trim(),
        log: 'false' // Don't re-log historical queries
      }
    })
  } else {
    // Route IP queries to SearchResults view
    let value = input.trim().split(",")
    router.push({
      name: 'SearchResults',
      query: {
        input: Array.isArray(value) ? value.join(',') : value,
        type: query_type,
        log: 'false' // Don't re-log historical queries
      }
    })
  }
}

function parseQuery(queryString) {
  const { cleanQuery, start, end } = extractTimeFromQuery(queryString)
  return {
    text: cleanQuery || queryString,
    timeLabel: formatTimeRange(start, end)
  }
}

// Format query type for display
function pretty_print(query_type) {
  if (query_type == "ip") return "Ip"
  else if (query_type == "ip_table") return "Ip Bulk"
  else if (query_type == "subdomains") return "Subdomains"
  else if (query_type == "emails") return "Emails"
  else if (query_type == "similar") return "Similar domains"
  else if (query_type == "reverse_dns") return "Reverse DNS"
  else if (query_type == "fingerprint_ips") return "Fingerprint to Ips"
  else if (query_type == "ip_fingerprints") return "Ip fingerprints"
  else if (query_type == "signals_toptalkers") return "Signals Toptalkers"
  else if (query_type == "event" || query_type == "event_search") return "Event Search"
  else return query_type
}

// Lifecycle hooks
onMounted(() => {
  fetchQueries()
})

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect()
  }
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
})
</script>

<style scoped>
.all-queries-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 100%;
}

/* Header */
.queries-header {
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #f3f4f6;
  margin: 0 0 0.5rem 0;
}

.total-count {
  font-size: 0.875rem;
  color: #9ca3af;
  font-weight: 500;
  min-height: 1.25rem;
}

/* Search & Filter */
.search-wrapper {
  padding: 1.5rem 2rem;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.search-row {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.search-input-container {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  font-size: 1rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #f3f4f6;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.3);
  background: rgba(26, 29, 36, 0.6);
}

.search-input::placeholder {
  color: #6b7280;
}

.time-filter {
  padding: 0.75rem 1rem;
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  color: #f3f4f6;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2.25rem;
}

.time-filter:focus {
  outline: none;
  border-color: rgba(168, 85, 247, 0.3);
  background-color: rgba(26, 29, 36, 0.6);
}

.time-filter option {
  background: #1a1d24;
  color: #f3f4f6;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  flex: 1;
}

.spinner {
  font-size: 2.5rem;
  color: #a855f7;
  margin-bottom: 1rem;
}

.loading-text {
  color: #9ca3af;
  font-size: 0.875rem;
  margin: 0;
}

/* Error State */
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  flex: 1;
}

.error-icon {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.error-title {
  font-size: 1rem;
  font-weight: 600;
  color: #ef4444;
  margin: 0 0 0.5rem 0;
}

.error-message {
  font-size: 0.875rem;
  color: #9ca3af;
  margin: 0 0 1.5rem 0;
  text-align: center;
}

.retry-button {
  padding: 0.625rem 1.5rem;
  background: rgba(168, 85, 247, 0.15);
  border: 1px solid rgba(168, 85, 247, 0.3);
  border-radius: 0.5rem;
  color: #a855f7;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(168, 85, 247, 0.25);
  border-color: rgba(168, 85, 247, 0.5);
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  flex: 1;
}

.empty-icon {
  font-size: 3rem;
  color: #6b7280;
  margin-bottom: 1rem;
  opacity: 0.5;
}

.empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #9ca3af;
  margin: 0 0 0.5rem 0;
}

.empty-description {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
  text-align: center;
}

/* Queries List */
.queries-list {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.history-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-chip {
  background: rgba(26, 29, 36, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: grid;
  grid-template-columns: minmax(120px, auto) minmax(0, 1fr) minmax(80px, auto);
  align-items: center;
  gap: 1rem;
}

.history-chip:hover {
  background: rgba(168, 85, 247, 0.08);
  border-color: rgba(168, 85, 247, 0.3);
}

.history-chip.event-type-chip:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.3);
}

.history-chip-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.history-type {
  color: #a855f7;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.history-type.event-type {
  color: #3b82f6;
}

.history-time {
  color: #6b7280;
  font-size: 0.7rem;
}

.history-query-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
}

.history-query {
  color: #c4b5fd;
  font-size: 0.875rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.time-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: rgba(115, 87, 210, 0.15);
  border-radius: 0.375rem;
  font-size: 0.675rem;
  color: #a78bfa;
  white-space: nowrap;
  flex-shrink: 0;
}

.history-credits {
  color: #6b7280;
  font-size: 0.75rem;
  text-align: right;
  white-space: nowrap;
}

.history-chip:hover .history-time,
.history-chip:hover .history-credits {
  color: #9ca3af;
}

/* Load More Trigger */
.load-more-trigger {
  height: 20px;
  margin: 20px 0;
}

/* Loading More */
.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.5rem;
  color: #9ca3af;
  font-size: 0.875rem;
}

.spinner-small {
  font-size: 1rem;
  color: #a855f7;
}

/* Scrollbar Styling */
.queries-list::-webkit-scrollbar {
  width: 8px;
}

.queries-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
}

.queries-list::-webkit-scrollbar-thumb {
  background: rgba(168, 85, 247, 0.3);
  border-radius: 4px;
  transition: background 0.2s ease;
}

.queries-list::-webkit-scrollbar-thumb:hover {
  background: rgba(168, 85, 247, 0.5);
}

/* Responsive Design */
@media (max-width: 768px) {
  .queries-header {
    padding: 1.5rem 1.5rem 0.75rem 1.5rem;
  }

  .header-title {
    font-size: 1.25rem;
  }

  .search-wrapper {
    padding: 1rem 1.5rem;
  }

  .queries-list {
    padding: 1.5rem;
  }

  .history-chip {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .history-chip-left {
    min-width: auto;
  }

  .history-credits {
    text-align: left;
  }
}
</style>
